/**
 * Style dla warstwy Plan Ogólny Gminy 2023 (POG).
 *
 * Źródła:
 *  - Kolory stref: QML z QGIS (CASE expression w StrefaPlanistyczna.qml,
 *    data-defined fillColor po polu `symbol`). Zachowane 1:1.
 *  - Nazwy stref: Dz.U. 2023 poz. 2758, załącznik do rozporządzenia POG,
 *    tabela stref planistycznych (13 pozycji SW..SK).
 *  - Minimalny udział PBC (Powierzchni Biologicznie Czynnej) + profile
 *    podstawowy/dodatkowy: ten sam załącznik.
 *
 * Obszary (OUZ, OZS, OSD) rysujemy jako tylko kontur (brak wypełnienia
 * kolorem, bo nachodzą na strefy) — w wersji docelowej kreskowanie
 * (hatch pattern) zgodnie z rozporządzeniem, na razie grube przerywane
 * linie w kolorze rgb(3,83,83).
 */

export type PlanZoneSymbol =
  | 'SW'
  | 'SJ'
  | 'SZ'
  | 'SU'
  | 'SH'
  | 'SP'
  | 'SR'
  | 'SI'
  | 'SN'
  | 'SC'
  | 'SG'
  | 'SO'
  | 'SK';

export type PlanAreaSymbol = 'POG' | 'OUZ' | 'OZS' | 'OSD';
export type PlanSymbol = PlanZoneSymbol | PlanAreaSymbol;

export interface PlanZoneStyle {
  symbol: PlanZoneSymbol;
  name: string;
  /** Fill: rgb(...) z QML-a. */
  fill: string;
  /** Fill z alpha (0.55) — do rysowania poligonu na mapie. */
  fillRgba: string;
  /** Outline: rgb(83,83,83) wspólny dla wszystkich stref. */
  stroke: string;
  strokeWidth: number;
  /** Minimalny udział powierzchni biologicznie czynnej [%]. null = nie dotyczy. */
  minPbc: number | null;
  /** Lista kodów profili podstawowych wg rozporządzenia (referencyjna). */
  basicProfiles: string[];
  /** Lista kodów profili dodatkowych wg rozporządzenia. */
  additionalProfiles: string[];
}

export interface PlanAreaStyle {
  symbol: PlanAreaSymbol;
  name: string;
  /** Dla obszarów: brak fill, tylko kontur + pattern. */
  stroke: string;
  strokeWidth: number;
  /** Wzór linii (zgodnie z QML i rozporządzeniem):
   *  - `'dashed-thick'` = gruba (3 mm) przerywana ramka POG, kreska 14 mm + odstęp 10 mm
   *  - `'dashed'`       = cienka przerywana (rezerwa)
   *  - `'hatch-v'`      = pionowe linie 90° (OUZ — Obszar Uzupełnienia Zabudowy)
   *  - `'hatch-h'`      = poziome linie 0° (OZS — Obszar Zabudowy Śródmiejskiej)
   *  - `'hatch-d135'`   = ukośne 135° (OSD — Obszar Standardów Dostępności)
   */
  pattern: 'dashed-thick' | 'dashed' | 'hatch-v' | 'hatch-h' | 'hatch-d135';
}

/** 13 stref wg rozporządzenia POG 2023. Kolejność zgodna z załącznikiem. */
export const PLAN_ZONES: Record<PlanZoneSymbol, PlanZoneStyle> = {
  SW: {
    symbol: 'SW',
    name: 'strefa wielofunkcyjna z zabudową mieszkaniową wielorodzinną',
    fill: 'rgb(154,124,100)',
    fillRgba: 'rgba(154,124,100,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 30,
    basicProfiles: [
      'teren zabudowy mieszkaniowej wielorodzinnej',
      'teren usług',
      'teren komunikacji',
      'teren zieleni urządzonej',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren zabudowy mieszkaniowej jednorodzinnej',
      'teren handlu wielkopowierzchniowego',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SJ: {
    symbol: 'SJ',
    name: 'strefa wielofunkcyjna z zabudową mieszkaniową jednorodzinną',
    fill: 'rgb(217,180,115)',
    fillRgba: 'rgba(217,180,115,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 30,
    basicProfiles: [
      'teren zabudowy mieszkaniowej jednorodzinnej',
      'teren usług',
      'teren komunikacji',
      'teren zieleni urządzonej',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren zabudowy letniskowej lub rekreacji indywidualnej',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SZ: {
    symbol: 'SZ',
    name: 'strefa wielofunkcyjna z zabudową zagrodową',
    fill: 'rgb(255,230,171)',
    fillRgba: 'rgba(255,230,171,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 30,
    basicProfiles: [
      'teren zabudowy zagrodowej',
      'teren produkcji w gospodarstwach rolnych',
      'teren akwakultury i obsługi rybactwa',
      'teren komunikacji',
      'teren zieleni urządzonej',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren wielkotowarowej produkcji rolnej',
      'teren rolnictwa z zakazem zabudowy',
      'teren biogazowni',
      'teren usług',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SU: {
    symbol: 'SU',
    name: 'strefa usługowa',
    fill: 'rgb(255,108,105)',
    fillRgba: 'rgba(255,108,105,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 30,
    basicProfiles: [
      'teren usług',
      'teren komunikacji',
      'teren zieleni urządzonej',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren składów i magazynów',
      'teren elektrowni słonecznej',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SH: {
    symbol: 'SH',
    name: 'strefa handlu wielkopowierzchniowego',
    fill: 'rgb(255,132,183)',
    fillRgba: 'rgba(255,132,183,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 30,
    basicProfiles: [
      'teren handlu wielkopowierzchniowego',
      'teren komunikacji',
      'teren zieleni urządzonej',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren usług',
      'teren składów i magazynów',
      'teren elektrowni słonecznej',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SP: {
    symbol: 'SP',
    name: 'strefa gospodarcza',
    fill: 'rgb(175,136,222)',
    fillRgba: 'rgba(175,136,222,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 20,
    basicProfiles: [
      'teren produkcji',
      'teren komunikacji',
      'teren zieleni urządzonej',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren usług',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SR: {
    symbol: 'SR',
    name: 'strefa produkcji rolniczej',
    fill: 'rgb(254,239,100)',
    fillRgba: 'rgba(254,239,100,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 30,
    basicProfiles: [
      'teren produkcji w gospodarstwach rolnych',
      'teren wielkotowarowej produkcji rolnej',
      'teren akwakultury i obsługi rybactwa',
      'teren komunikacji',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren rolnictwa z zakazem zabudowy',
      'teren biogazowni',
      'teren elektrowni słonecznej',
      'teren elektrowni wiatrowej',
      'teren elektrowni wodnej',
      'teren zieleni urządzonej',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SI: {
    symbol: 'SI',
    name: 'strefa infrastrukturalna',
    fill: 'rgb(204,204,204)',
    fillRgba: 'rgba(204,204,204,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 20,
    basicProfiles: [
      'teren infrastruktury technicznej',
      'teren komunikacji',
      'teren ogrodów działkowych',
    ],
    additionalProfiles: [
      'teren usług',
      'teren produkcji',
      'teren zieleni urządzonej',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SN: {
    symbol: 'SN',
    name: 'strefa zieleni i rekreacji',
    fill: 'rgb(187,245,139)',
    fillRgba: 'rgba(187,245,139,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 50,
    basicProfiles: [
      'teren zieleni urządzonej',
      'teren plaży',
      'teren wód',
      'teren komunikacji',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren usług sportu i rekreacji',
      'teren usług kultury i rozrywki',
      'teren usług handlu detalicznego',
      'teren usług gastronomii',
      'teren usług turystyki',
      'teren usług nauki',
      'teren usług edukacji',
      'teren usług zdrowia i pomocy społecznej',
      'teren zieleni naturalnej',
      'teren lasu',
    ],
  },
  SC: {
    symbol: 'SC',
    name: 'strefa cmentarzy',
    fill: 'rgb(148,212,196)',
    fillRgba: 'rgba(148,212,196,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: 30,
    basicProfiles: [
      'teren cmentarza',
      'teren komunikacji',
      'teren zieleni urządzonej',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren usług kultu religijnego',
      'teren usług handlu detalicznego',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SG: {
    symbol: 'SG',
    name: 'strefa górnictwa',
    fill: 'rgb(252,212,255)',
    fillRgba: 'rgba(252,212,255,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: null,
    basicProfiles: [
      'teren górnictwa i wydobycia',
      'teren komunikacji',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren produkcji',
      'teren usług handlu',
      'teren usług rzemieślniczych',
      'teren usług gastronomii',
      'teren usług biurowych i administracji',
      'teren usług nauki',
      'teren zieleni urządzonej',
      'teren zieleni naturalnej',
      'teren lasu',
      'teren wód',
    ],
  },
  SO: {
    symbol: 'SO',
    name: 'strefa otwarta',
    fill: 'rgb(227,255,199)',
    fillRgba: 'rgba(227,255,199,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: null,
    basicProfiles: [
      'teren rolnictwa z zakazem zabudowy',
      'teren lasu',
      'teren zieleni naturalnej',
      'teren wód',
      'teren komunikacji',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren elektrowni wiatrowej',
      'teren elektrowni słonecznej',
      'teren elektrowni geotermalnej',
      'teren elektrowni wodnej',
      'teren biogazowni',
      'teren zieleni urządzonej',
    ],
  },
  SK: {
    symbol: 'SK',
    name: 'strefa komunikacyjna',
    fill: 'rgb(242,242,242)',
    fillRgba: 'rgba(242,242,242,0.55)',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 0.8,
    minPbc: null,
    basicProfiles: [
      'teren autostrady',
      'teren drogi ekspresowej',
      'teren drogi głównej ruchu przyspieszonego',
      'teren drogi głównej',
      'teren komunikacji kolejowej i szynowej',
      'teren komunikacji kolei linowej',
      'teren komunikacji wodnej',
      'teren komunikacji lotniczej',
      'teren obsługi komunikacji',
      'teren ogrodów działkowych',
      'teren infrastruktury technicznej',
    ],
    additionalProfiles: [
      'teren drogi zbiorczej',
      'teren usług handlu detalicznego',
      'teren usług turystyki',
      'teren usług gastronomii',
      'teren zieleni urządzonej',
      'teren lasu',
      'teren zieleni naturalnej',
      'teren wód',
    ],
  },
};

/** 4 obszary specjalne (nie są strefami planistycznymi). */
export const PLAN_AREAS: Record<PlanAreaSymbol, PlanAreaStyle> = {
  POG: {
    symbol: 'POG',
    name: 'obszar objęty planem ogólnym gminy',
    stroke: 'rgb(3,83,83)',
    strokeWidth: 3,
    pattern: 'dashed-thick',
  },
  OUZ: {
    symbol: 'OUZ',
    name: 'obszar uzupełnienia zabudowy',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 1,
    pattern: 'hatch-v', // QML: angle=90 (linie pionowe), distance=2 mm, line_width=0.26
  },
  OZS: {
    symbol: 'OZS',
    name: 'obszar zabudowy śródmiejskiej',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 1,
    pattern: 'hatch-h', // QML: angle=0 (linie poziome), distance=2 mm
  },
  OSD: {
    symbol: 'OSD',
    name: 'obszar standardów dostępności infrastruktury społecznej',
    stroke: 'rgb(83,83,83)',
    strokeWidth: 1,
    pattern: 'hatch-d135', // QML: angle=135 (ukośne), distance=2 mm
  },
};

export const ALL_ZONE_SYMBOLS = Object.keys(PLAN_ZONES) as PlanZoneSymbol[];
export const ALL_AREA_SYMBOLS = Object.keys(PLAN_AREAS) as PlanAreaSymbol[];

/** Pomocnicza funkcja — zwraca styl dla dowolnego symbolu (strefa/obszar) albo null. */
export function getPlanStyle(symbol: string): PlanZoneStyle | PlanAreaStyle | null {
  if (symbol in PLAN_ZONES) return PLAN_ZONES[symbol as PlanZoneSymbol];
  if (symbol in PLAN_AREAS) return PLAN_AREAS[symbol as PlanAreaSymbol];
  return null;
}

/** Czy dany symbol jest strefą planistyczną (ma fill). */
export function isZoneSymbol(symbol: string): symbol is PlanZoneSymbol {
  return symbol in PLAN_ZONES;
}
